import React, { useState } from "react";
import { Button, Input, message } from "antd";
import "./../assets/OnboardLogMask.css";
import uploadIcon from '../assets/upload.svg';
import selectedFileIcon from '../assets/selected_file.svg';

function OnboardLogMask({ userInfo }) {
  const [logMask, setLogMask] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  
  // State variables for two-step confirmation flow
  const [currentStep, setCurrentStep] = useState(1);
  const [validationError, setValidationError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".cfg")) {
      message.error("Please select a .cfg file");
      return;
    }
    setSelectedFile(file);
    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogMask(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!file.name.endsWith(".cfg")) {
      message.error("Please select a .cfg file");
      return;
    }
    setSelectedFile(file);
    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogMask(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleContinue = () => {
    if (!logMask) {
      setValidationError("Log mask file is required");
      return;
    }
    if (!description.trim()) {
      setValidationError("Description is required");
      return;
    }
    
    setValidationError("");
    setCurrentStep(2);
  };

  const handleGoBack = () => {
    setCurrentStep(1);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleSubmit = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_WBC_API_BASE_URL}/api/v1/onboarding/log-mask`,
        {
          method: "POST",
          body: (() => {
            const formData = new FormData();
            formData.append("description", description.trim());
            formData.append("logMask", selectedFile);
            formData.append("userEmailId", userInfo);
            return formData;
          })(),
        }
      );
      if (resp.ok) {
        setSuccessMessage("Log mask onboarded successfully!");
      } else {
        // Only parse JSON for error responses
        try {
          const data = await resp.json();
          setErrorMessage(data?.errorMessage || "Failed to onboard log mask");
        } catch {
          setErrorMessage("Failed to onboard log mask");
        }
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Error onboarding log mask");
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedFile(null);
    setSelectedFileName("");
    setLogMask("");
    setDescription("");
    setCurrentStep(1);
    setValidationError("");
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleStartNew = () => {
    setSelectedFile(null);
    setSelectedFileName("");
    setLogMask("");
    setDescription("");
    setCurrentStep(1);
    setValidationError("");
    setSuccessMessage("");
    setErrorMessage("");
  };

  if (currentStep === 1) {
    return (
      <div className="onboard-log-mask-container">
        <div className="tab-heading">
          <h1>Onboard a Log Mask</h1>
        </div>
        
        {validationError && (
          <div className="error-banner">
            <div className="error-message">{validationError}</div>
          </div>
        )}
        
        <div className="input-container" style={{ marginBottom: "12px" }}>
          <label
            htmlFor="choose-file-to-upload-cfg-button"
            className="custom-label"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {selectedFileName ? (
              <>
                <img className="upload-icon" src={selectedFileIcon} alt="Selected file" />
                {selectedFileName}
              </>
            ) : (
              <>
                <img className="upload-icon" src={uploadIcon} alt="Upload Icon" />
                Click to select a cfg file or drag and drop the cfg file here
              </>
            )}
          </label>
          <input
            id="choose-file-to-upload-cfg-button"
            className="choose-file-button custom-input"
            type="file"
            accept=".cfg"
            onChange={handleFileChange}
          />
        </div>
        <Input.TextArea
          placeholder="Enter description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={{ marginBottom: "12px" }}
        />
        <div className="submit-button-section">
          <button
            className="submit-button"
            onClick={handleContinue}
            disabled={!logMask || !description.trim()}
          >
            Continue
          </button>
          <button
            onClick={handleClearSelection}
            style={{ marginLeft: '12px', background: 'none', border: '1.5px solid transparent', color: '#6b7280', fontSize: '13px', fontWeight: 500, padding: '8px 20px', borderRadius: '6px', cursor: 'pointer' }}
          >
            Clear Selection
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Review Page
  return (
    <div className="onboard-log-mask-container">
      <div className="tab-heading">
        <h1>Onboard a Log Mask</h1>
      </div>
      
      <div className="review-section">
        <h3>Review your selection:</h3>
        
        <div className="review-cards">
          <div className="review-card">
            <h4>Log Mask Onboarding Request</h4>
            <div className="review-card-content">
              <div className="review-item">
                <span className="review-label">Selected File:</span>
                <span className="review-value">{selectedFileName}</span>
              </div>
              <div className="review-item">
                <span className="review-label">Description:</span>
                <span className="review-value">{description}</span>
              </div>
              <div className="review-item">
                <span className="review-label">User:</span>
                <span className="review-value">{userInfo}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {successMessage && (
        <div className="success-banner">
          <div className="success-message">{successMessage}</div>
          <button className="submit-button" onClick={handleStartNew}>
            Start New Request
          </button>
        </div>
      )}
      
      {errorMessage && (
        <div className="error-banner">
          <div className="error-message">{errorMessage}</div>
        </div>
      )}
      
      {!successMessage && !errorMessage && (
        <div className="review-actions">
          <button className="manage-systems-go-back-button" onClick={handleGoBack}>
            &lt; Go Back
          </button>
          <button 
            className="submit-button" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
}

export default OnboardLogMask;
